/**
 * walletMapping.js — the ONE place `GET /api/wallet/:userId` becomes `Wallet.jsx` props (#579 / AD-07).
 *
 * `Wallet.jsx` is presentational by contract: its own header says it "never fetches or navigates".
 * Every number it renders therefore arrives from here, and every number it renders is a claim about
 * a child's money. The falsifying cases for each claim live in `walletMapping.test.js`, and they
 * were written before the code they test.
 *
 * **The wire types, because getting them wrong is silent.** `wallet.service.js:63` is
 * `const money = (value) => Number(value).toFixed(2)`, so **every amount crosses the wire as a
 * two-decimal STRING** — `"6.25"`, never `6.25`. Percentages and ids are NUMBERS: `toJars` builds
 * them with `Number(...)` and the goal's `percent` is a `Math.round`. The first version of this
 * file's tests used numbers for money throughout, so the whole suite ran against a payload shape
 * the server does not send.
 *
 * **Three sentences about money, kept apart.** The payload separates them deliberately
 * (`server/services/wallet.service.js:216-232`) and this file never joins them:
 *
 * | field | means | where it may appear |
 * | --- | --- | --- |
 * | `balance` | settled — already in the jar | the hero, alone |
 * | `earned` | approved by a parent, no settled payout row yet | the pending card, named as approved |
 * | `pending` | awaiting a parent's decision | the pending card, named as waiting |
 *
 * `Wallet.jsx`'s HONESTY RULE is that the hero shows the settled total ONLY. Folding `earned` into
 * it tells a child money is theirs while a parent still holds it.
 *
 * **The jars are a SPLIT, not a balance.** `toJars` answers `save_pct` / `share_pct` / `spend_pct`
 * — the parent-set percentages. `JarMeter` takes an `amount` and renders it as money, so passing a
 * percentage as an amount puts "$50.00" under the Save jar of a child who has $3.50. No per-jar
 * balance exists in the payload at all: the service computes a `saved` column and does not return
 * it, and summing `payout_history[].jars` would total only the page the cursor happened to return.
 * So no jar here carries an `amount`, and `JarMeter` renders no money line when it is absent
 * (`JarMeter.jsx:403`). The SCREEN may still show one: since #583 N1 S14, `WalletRoute.jsx` adds each
 * jar's ALLOCATED total from `GET /api/wallet/:userId/jar-history` (stored settled items, never this
 * split), and only when all three jar reads are readable. That amount never comes from this file.
 *
 * **A payday is reported, never narrated.** The rows carry periods and amounts. They carry no
 * chapter number and no story sentence, and numbering by array position breaks the moment a cursor
 * is used, because page two's first row is not chapter one.
 *
 * **UNREADABLE IS NEVER EMPTY, and that rule cost a whole review round to learn properly.** The
 * first version read every field with a bare `Number()`, which answers **0** for `false`, for `[]`
 * and for `'   '`, and it turned a missing `payout_history` into the same empty timeline a
 * brand-new child legitimately has. A corrupt payload therefore rendered as a child with no money
 * and no history — a confident, false, and completely silent sentence. Every reader below is
 * strict, and a required field this cannot read makes the WHOLE view `null`, which the route
 * renders as an error.
 */

/** Said when a saving-goal response arrived but could not be read. Not "you have no goal". */
export const GOAL_UNREADABLE = 'We couldn’t read your saving goal just now — your coins are safe.';

/** Three jars, in the order the family set them, with the page's own kind vocabulary. */
const JARS = Object.freeze([
  { kind: 'save', name: 'Save', field: 'save_pct' },
  { kind: 'share', name: 'Share', field: 'share_pct' },
  { kind: 'spend', name: 'Spend', field: 'spend_pct' },
]);

/** Chip colours, one per jar, matching the kinds `Chip` already ships. */
const JAR_CHIP_KIND = Object.freeze({ save: 'gold', share: 'violet', spend: 'ember' });

/** A bare calendar date, which is what a hold's `due_on` is (`to_char(..., 'YYYY-MM-DD')`). */
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A plain decimal. Deliberately NOT what `Number()` accepts.
 *
 * `Number('0x10')` is 16 and `Number('1e5')` is 100000, so a malformed field could otherwise become
 * a plausible amount rather than an obvious failure.
 *
 * **At most TWO decimals, and that clause is load-bearing.** `money()` is `Number(v).toFixed(2)`, so
 * a third decimal is corruption. It mattered once the payout sum check landed: `cents()` ROUNDS, so
 * an amount of `"1.004"` above parts totalling `"1.00"` compared equal and rendered as a consistent
 * payday. Refusing the precision refuses the whole class.
 */
const DECIMAL = /^-?\d+(\.\d{1,2})?$/;

/**
 * An ISO 4217 code, in EITHER case.
 *
 * An earlier version required `^[A-Z]{3}$` and cited `tenants.currency` for it. That citation was
 * wrong: `tenants.currency` is `varchar(3) NOT NULL DEFAULT 'USD'`, which permits lowercase and
 * shorter values. The uppercase CHECK belongs to `payouts.currency`, a different column. So the
 * strict form would have refused a wallet the database considers perfectly valid. Three letters,
 * any case, normalized to upper.
 */
const CURRENCY_CODE = /^[A-Za-z]{3}$/;

/**
 * What this screen does not have a contract for, said in the one note the page already renders. ONE source:
 * beside S14's allocated jar amounts, `WalletRoute.jsx` says the same sentence without the jar clause.
 */
const NOT_BUILT = 'your next payday date and your streak are not built into this screen yet.';
const NOT_SHOWN = `Your jar balances, ${NOT_BUILT}`;
export const NOT_BUILT_BESIDE_ALLOCATIONS = `Y${NOT_BUILT.slice(1)}`;

/**
 * A money figure, or `null` when the value is not one.
 *
 * `null` here is never "zero". A caller that cannot tell the two apart would render an unreadable
 * payload as a child with no money, which is the failure this whole file exists to refuse.
 * Accepts the two-decimal string the service sends, and a finite number for the callers that send
 * one. Refuses everything else, including `false`, `[]`, `'   '` and hex or exponent spellings.
 */
function readMoney(value) {
  if (typeof value === 'number') {
    // The number branch used to skip the precision rule entirely, so a numeric `1.004` sailed past
    // while the string `'1.004'` was refused — and `cents()` then rounded both sides of a payout
    // sum to the same figure and called the payday consistent. Round-trip through cents instead of
    // testing the digits, which a number does not have.
    return Number.isFinite(value) && Math.round(value * 100) / 100 === value ? value : null;
  }
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!DECIMAL.test(text)) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

/**
 * A whole-number percentage between 0 and 100, or `null`.
 *
 * `jar_save_percent` is `smallint NOT NULL`, so a fraction, a negative or a value above 100 cannot
 * come from a healthy server. The first version accepted all three and rendered them as the
 * parent-set allocation.
 */
function readPercent(value) {
  const n = readMoney(value);
  if (n === null || !Number.isInteger(n) || n < 0 || n > 100) return null;
  return n;
}

/**
 * Money in whole cents, for comparing sums without float error.
 *
 * `4.25 + 0.85 + 3.40` is `8.500000000000002` in IEEE 754, so comparing money by subtraction
 * rejects a payday that balances perfectly. Every amount on this contract is two decimals.
 */
const cents = (n) => Math.round(n * 100);

/** A tenant currency, or `null`. An empty string is not a currency, and it used to pass. */
function readCurrency(value) {
  if (typeof value !== 'string' || !CURRENCY_CODE.test(value)) return null;
  return value.toUpperCase();
}

/** Two ids name the same member. The wire mixes string and number ids, so compare as strings. */
function sameMember(a, b) {
  if (a === null || a === undefined || a === '') return false;
  if (b === null || b === undefined || b === '') return false;
  return String(a) === String(b);
}

/**
 * Format money for a sentence.
 *
 * The currency is validated before anything reaches here, so there is no currency-free branch: a
 * payload without one never becomes a view. An unknown-but-well-formed code falls back to digits
 * plus the code rather than throwing into the render.
 */
function say(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${Number(amount).toFixed(2)} ${currency}`;
  }
}

/**
 * Name a bare family-local calendar day (a hold's `due_on`) WITHOUT moving it.
 *
 * `new Date('2026-10-02')` is midnight UTC, and formatting that instant in a zone west of UTC
 * renders **October 1st**. So the parts are read off the string and re-anchored at UTC, and the
 * formatter is told UTC too. No zone conversion happens at any point, which is the only way this
 * cannot drift. (A payout's period end is an instant, not a day: `paydayLabel` below.)
 */
function calendarDayLabel(periodEnd) {
  const m = CALENDAR_DATE.exec(String(periodEnd ?? ''));
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]) - 1, Number(m[3])];
  const at = new Date(Date.UTC(y, mo, d));
  // The regular expression accepts `2026-02-31`, which `Date.UTC` NORMALIZES to March 3rd — a
  // payday silently moved five weeks. Round-trip the parts to refuse a date that does not exist.
  if (at.getUTCFullYear() !== y || at.getUTCMonth() !== mo || at.getUTCDate() !== d) return null;
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC',
    }).format(at);
  } catch {
    return periodEnd;
  }
}

/**
 * Name the payday that closed a period, in the household's own zone.
 *
 * `payouts.period_end` is `timestamptz` (migration 1788818164074), so node-pg hands
 * `wallet.service.js` a `Date` and JSON writes it with `toISOString()`: always milliseconds, always
 * `Z`. This file read it as a bare date until the #583 live run, where every child's Wallet broke
 * after their first real payday (D-A). Only that one spelling is accepted: the round trip below
 * refuses a bare date, another offset, missing milliseconds and a day `Date` would move.
 *
 * A period runs from midnight of one payday to midnight of the next, household time
 * (`payday_period_start`, migration 1788929031127), and `period_end` is that second midnight. So its
 * local date IS the payday: Monday for a `weekly_monday` family. Formatting the instant in UTC
 * names the day before for every family east of UTC, so the row's own `period_timezone` is required,
 * and a zone `Intl` cannot use makes the row unreadable rather than quietly local.
 */
function paydayLabel(periodEnd, timeZone) {
  if (typeof periodEnd !== 'string') return null;
  const at = new Date(periodEnd);
  if (!Number.isFinite(at.getTime()) || at.toISOString() !== periodEnd) return null;
  if (typeof timeZone !== 'string' || !timeZone) return null;
  try {
    return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone }).format(at);
  } catch {
    return null;
  }
}

/**
 * The pending card, or `null` when a child has nothing outstanding.
 *
 * `amount` is the AWAITING-REVIEW figure only, and it is omitted when that figure is zero so the
 * card never crowns a "$0.00". The approved-but-unpaid figure is a different sentence and gets its
 * own clause. The two are never added together.
 */
function toPendingCard(awaitingReview, approvedUnpaid, currency, held = []) {
  if (!(awaitingReview > 0) && !(approvedUnpaid > 0)) return null;

  const clauses = [];
  if (awaitingReview > 0) {
    clauses.push(`${say(awaitingReview, currency)} is waiting for a grown-up to say yes.`);
  }
  // Present but unreadable hold metadata cannot promise that all approved money lands on payday.
  if (held === null) {
    if (approvedUnpaid > 0) clauses.push(`${say(approvedUnpaid, currency)} is already approved. We couldn’t read when it will be paid. A grown-up can check on it.`);
  }
  // #580 4b2: held money is part of `earned`, so only the known unheld rest lands on payday.
  const unheld = held === null ? 0 : (cents(approvedUnpaid) - held.reduce((sum, h) => sum + cents(h.amount), 0)) / 100;
  if (unheld > 0) {
    clauses.push(`${say(unheld, currency)} is already approved and lands on payday.`);
  }
  for (const h of held || []) {
    clauses.push(h.late
      ? `${say(h.amount, currency)} is being kept for later. It was due by ${h.dueLabel} and is running late. It is still yours, and a grown-up can check on it.`
      : `${say(h.amount, currency)} is being kept for later. It comes by ${h.dueLabel}.`);
  }

  return {
    // Kept as numbers so a caller can assert on them without parsing a sentence.
    awaitingReview,
    approvedUnpaid,
    amount: awaitingReview > 0 ? awaitingReview : undefined,
    caption: awaitingReview > 0 ? 'is waiting for high-fives' : undefined,
    description: clauses.join(' '),
  };
}

/**
 * #580 F-08 slice 4b2 — `held_for_later`, this child's own money a parent chose to Wait on: an
 * amount, the family-local day it is due and whether it is late. It is part of `approvedUnpaid`.
 * Absent metadata supports older servers. Present but unreadable metadata is null, so the card
 * preserves the approved amount while admitting that its payment timing could not be read.
 */
function readHeld(value, approvedUnpaid) {
  if (value === undefined) return [];
  if (!Array.isArray(value)) return null;
  const held = [];
  for (const entry of value) {
    const amount = entry && typeof entry === 'object' ? readMoney(entry.amount) : null;
    const dueLabel = entry && typeof entry === 'object' ? calendarDayLabel(entry.due_on) : null;
    if (!(amount > 0) || dueLabel === null || typeof entry.late !== 'boolean') return null;
    held.push({ amount, dueLabel, late: entry.late });
  }
  return held.reduce((sum, h) => sum + cents(h.amount), 0) <= cents(approvedUnpaid) ? held : null;
}

/**
 * One settled payday, reported. `null` when the row cannot be read — the caller fails the view.
 *
 * **The row's OWN currency is used, not the wallet's.** Every `payout_history` row carries one, and
 * nothing in the contract says a family may never change currency. Labelling a historical payday in
 * today's currency would silently restate what a child was actually paid.
 *
 * **The three jar amounts must sum to the payout amount.** An earlier version's comment claimed to
 * refuse "a payday whose split silently does not add up to its own total" and then did not check
 * it, so a row reading `$8.50` above `$4.25 + $0.85 + $9.99` rendered as a normal payday.
 */
function toChapter(row, isLatest) {
  if (!row || typeof row !== 'object') return null;

  const currency = readCurrency(row.currency);
  if (currency === null) return null;

  const earned = readMoney(row.amount);
  if (earned === null) return null;

  const date = paydayLabel(row.period_end, row.period_timezone);
  if (date === null) return null;

  const jars = row.jars && typeof row.jars === 'object' ? row.jars : {};
  const chips = [];
  let split = 0;
  for (const { kind, name } of JARS) {
    const part = readMoney(jars[kind]);
    // A row that names a jar it cannot value is unreadable.
    if (part === null) return null;
    split += cents(part);
    chips.push({ kind: JAR_CHIP_KIND[kind], label: `${name} ${say(part, currency)}` });
  }
  if (split !== cents(earned)) return null;

  return {
    id: row.id,
    // `kind` is the server's own word for what this row is. NOT a narrative title.
    title: typeof row.kind === 'string' && row.kind ? row.kind.replace(/^./, (c) => c.toUpperCase()) : undefined,
    date,
    periodEnd: row.period_end ?? null,
    periodStart: row.period_start ?? null,
    earned,
    currency,
    chips,
    latest: isLatest,
    // `chapter` and `story` are deliberately ABSENT. Neither exists in the payload, and
    // `PaydayChapterEntry` renders without them.
  };
}

/**
 * The saving goal, or `null`. Never an estimate of how long it will take.
 *
 * A goal whose numbers cannot be read does NOT fail the whole wallet — it is a second, smaller
 * claim. It renders a card carrying only a message, which `Wallet.jsx` supports (`goalObj.message`),
 * so the child is told the goal could not be read rather than shown a wallet that quietly lost it.
 */
function toGoal(goalPayload) {
  // No read was attempted at all.
  if (goalPayload === null || goalPayload === undefined) return null;

  // A FULFILLED request with a broken body is not "this child has no goal". Only an explicit
  // `{ goal: null }` says that, and the two reached the same answer before.
  if (typeof goalPayload !== 'object' || !('goal' in goalPayload)) return { message: GOAL_UNREADABLE };

  const goal = goalPayload.goal;
  if (goal === null) return null;
  if (!goal || typeof goal !== 'object') return { message: GOAL_UNREADABLE };

  const saved = readMoney(goal.currentBalance);
  const target = readMoney(goal.targetAmount);
  const percent = readPercent(goal.percent);

  // `percent` is part of the documented goal shape, so an unreadable one is an unreadable goal —
  // not a goal rendered without its progress.
  if (!goal.label || saved === null || target === null || percent === null) {
    return { message: GOAL_UNREADABLE };
  }

  return {
    title: goal.label,
    saved,
    goal: target,
    percent,
    // `weeksLeft` is deliberately ABSENT. Nothing in the contract measures it.
  };
}

/**
 * Turn one wallet payload (and, when it was read, one saving-goal payload) into `Wallet.jsx` props.
 *
 * Answers `null` for a payload this cannot read. `null` means the READ FAILED — the caller must
 * render an error, never an empty wallet, because "you have no money" and "we could not read your
 * money" are different sentences and only one of them is true.
 *
 * @param {object|null} payload       `GET /api/wallet/:userId`
 * @param {object|null} [goalPayload] `GET /api/wallet/:userId/saving-goal`
 * @param {{isFirstPage?: boolean, expectUserId?: string|number}} [opts]
 *        `isFirstPage` false when a cursor was sent, so no row is marked as the newest payday —
 *        page two's first row is not. `expectUserId` fails the view closed when the payload (or the
 *        goal) describes a DIFFERENT member: `selfOrParent` authorizes the request server-side, so
 *        this guards a stale cache, a routing defect or a tenant-boundary failure, not an API hole.
 */
export function toWalletView(payload, goalPayload = null, { isFirstPage = true, expectUserId } = {}) {
  if (!payload || typeof payload !== 'object') return null;

  if (expectUserId !== undefined && !sameMember(payload.user_id, expectUserId)) return null;

  const currency = readCurrency(payload.currency);
  if (currency === null) return null;

  // Three REQUIRED money fields. A missing one is unreadable, never a zero.
  const balance = readMoney(payload.balance);
  const awaitingReview = readMoney(payload.pending);
  const approvedUnpaid = readMoney(payload.earned);
  if (balance === null || awaitingReview === null || approvedUnpaid === null) return null;

  const split = payload.jars;
  if (!split || typeof split !== 'object') return null;
  // `toJars` stamps the split with its OWN `user_id`. Checking only the wallet's top-level id left
  // a payload that pairs this child's wallet with another child's allocation passing the guard.
  if (expectUserId !== undefined && !sameMember(split.user_id, expectUserId)) return null;
  const jars = [];
  let allocated = 0;
  for (const { kind, name, field } of JARS) {
    const level = readPercent(split[field]);
    if (level === null) return null;
    allocated += level;
    // NO `amount`. The payload holds no per-jar balance, and a percentage in that slot renders
    // as money.
    jars.push({ id: kind, kind, name, level });
  }
  // Each share was range-checked alone, so 60/60/0 passed and was described as the allocation used
  // for every payday. An allocation that is not the whole of the money is not an allocation.
  if (allocated !== 100) return null;

  const jarsSplitNote =
    `Your grown-ups set your jars: ${jars.map((j) => j.level).join(' / ')} — every payday splits `
    + `itself. ${NOT_SHOWN}`;

  // An ABSENT history is not a child who never had a payday.
  if (!Array.isArray(payload.payout_history)) return null;
  const chapters = [];
  for (let i = 0; i < payload.payout_history.length; i += 1) {
    const chapter = toChapter(payload.payout_history[i], isFirstPage && i === 0);
    if (chapter === null) return null;
    chapters.push(chapter);
  }

  // ORDER MATTERS, and getting it wrong was a fix-induced regression. A malformed goal body has no
  // `userId`, so checking ownership FIRST made `{ goal: 'nope' }` fail `sameMember` and null the
  // entire wallet — the opposite of the rule that a goal is a smaller claim. Ownership is asserted
  // only on a WELL-FORMED goal object. A malformed one is reported by `toGoal`, not fatal.
  // `typeof {} === 'object'` and so is `[]`, so the previous narrowing still let an EMPTY goal
  // object into the ownership branch, where its missing `userId` nulled the whole wallet. Ownership
  // is asserted only on a goal that actually CARRIES an owner. One that does not is malformed, and
  // `toGoal` reports it on the card.
  const rawGoal = goalPayload && typeof goalPayload === 'object' ? goalPayload.goal : null;
  const goalNamesAnOwner = !!rawGoal && typeof rawGoal === 'object' && !Array.isArray(rawGoal)
    && rawGoal.userId !== undefined && rawGoal.userId !== null && rawGoal.userId !== '';
  if (goalNamesAnOwner && expectUserId !== undefined && !sameMember(rawGoal.userId, expectUserId)) {
    return null;
  }

  return {
    userId: payload.user_id ?? null,
    balance,
    currency,
    pending: toPendingCard(awaitingReview, approvedUnpaid, currency, readHeld(payload.held_for_later, approvedUnpaid)),
    jars,
    jarsSplitNote,
    goal: toGoal(goalPayload),
    chapters,
    nextCursor: payload.next_cursor ?? null,
  };
}

// The strict readers, shared with `walletStoryMapping.js` (#583 N1 S14) so money is read one way.
export { readMoney, readCurrency, sameMember, say, cents };

export default toWalletView;
